import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import DesktopView from "../layout/DesktopView";
import SimulantCard from "./SimulantCard";
import PromptEditor from "../prompts/PromptEditor";
import { createPrompt, updatePrompt } from "../../services/promptService";
import { useToast } from "../common/ToastContext";
import { useCardSelector } from "../common/CardSelectorProvider";
import { getPrompts } from "../../services/promptService";

const SimulantDesktop = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [simulants, setSimulants] = useState([]);
  const [selectedSimulant, setSelectedSimulant] = useState(null);
  const [goBack, setGoBack] = useState(null);
  const { showToast } = useToast();
  const { openCardSelector } = useCardSelector();

  const fetchSimulants = useCallback(async () => {
    try {
      const [simulants, searchKey] = await Promise.all([
        getPrompts("simulant"),
        searchParams.get("key"),
      ]);
      setSimulants(simulants);

      if (searchKey) {
        const editing = simulants.find((e) => e.key === searchKey);
        if (editing) {
          setSelectedSimulant(editing);
          if (searchParams.has("goback")) {
            const goback = searchParams.get("goback");
            setGoBack(goback);
            setSearchParams((prevParams) => {
              const newParams = new URLSearchParams(prevParams);
              newParams.delete("goback");
              return newParams;
            });
          }
        } else {
          setSelectedSimulant(null);
          setSearchParams((prevParams) => {
            const newParams = new URLSearchParams(prevParams);
            newParams.delete("key");
            return newParams;
          });
        }
      } else {
        setSelectedSimulant(null);
      }
    } catch (error) {
      console.error("Error fetching simulants:", error);
      showToast("Failed to fetch simulants", "error");
    }
  }, [searchParams, setSearchParams, showToast]);

  useEffect(() => {
    fetchSimulants();
  }, [fetchSimulants, location.pathname]);

  useEffect(() => {
    window.addEventListener("simulantCreated", fetchSimulants);
    return () => {
      window.removeEventListener("simulantCreated", fetchSimulants);
    };
  }, []);

  const handleClosePromptEditor = useCallback(async () => {
    if (goBack) {
      const goto = goBack;
      setGoBack(null);
      navigate(goto + "?reload=true");
    } else {
      await fetchSimulants();
      setSearchParams({});
      setSelectedSimulant(null);
    }
  }, [setSearchParams, goBack, navigate, fetchSimulants]);

  const handleAddNew = useCallback(async () => {
    const randomFourDigit = Math.floor(1000 + Math.random() * 9000);
    try {
      const simulant = await createPrompt({
        type: "simulant",
        name: `New Simulant ${randomFourDigit}`,
        description: "",
      });
      await fetchSimulants();
      setSelectedSimulant(simulant);
      setSearchParams((prevParams) => {
        const newParams = new URLSearchParams(prevParams);
        newParams.set("key", simulant.key);
        return newParams;
      });
      showToast("New simulant created successfully", "success");
    } catch (error) {
      console.error("Error creating new simulant:", error);
      showToast("Failed to create new simulant", "error");
    }
  }, [fetchSimulants, setSearchParams, showToast]);

  const handleClick = useCallback(
    (simulant) => {
      setSelectedSimulant(simulant);
      setSearchParams((prevParams) => {
        const newParams = new URLSearchParams(prevParams);
        newParams.set("key", simulant.key);
        return newParams;
      });
    },
    [setSearchParams]
  );

  const handleSearch = useCallback((term) => {
    // This function is left empty as search is handled internally by DesktopView
  }, []);

  const handleSave = useCallback((data) => {
    // Implement save logic if needed
  }, []);

  const handleDelete = useCallback((id) => {
    // Implement delete logic if needed
  }, []);

  const handleReset = useCallback((id) => {
    // Implement reset logic if needed
  }, []);

  const handleRestoreDeleted = useCallback(async () => {
    const all = await getPrompts("simulant", true);
    const deleted = all.filter((d) => d.deleted);
    const sorted = deleted.sort((a, b) => {
      return new Date(b.updated_at) - new Date(a.updated_at);
    });
    openCardSelector({
      items: sorted,
      CardComponent: SimulantCard,
      title: "Select a Deleted Simulant to Restore",

      onSelect: (s) => {
        const restored = JSON.parse(JSON.stringify(s));
        restored.name = `${s.name} (Restored ${new Date().toLocaleString()})`;
        restored.deleted = false;
        updatePrompt(restored).then(() => {
          fetchSimulants();
          showToast(
            `Simulant restored successfully as ${restored.name}`,
            "success"
          );
        });
      },
    });
  }, []);

  return (
    <DesktopView
      items={simulants}
      selectedItem={selectedSimulant}
      onSearch={handleSearch}
      searchPlaceholder="Filter simulants ..."
      contextMenuItems={[
        {
          label: "Restore Deleted",
          onClick: () => {
            handleRestoreDeleted();
          },
        },
      ]}
      onItemClick={handleClick}
      onSave={handleSave}
      onDelete={handleDelete}
      onReset={handleReset}
      onClose={handleClosePromptEditor}
      Card={SimulantCard}
      Editor={PromptEditor}
      addNewItem={handleAddNew}
      eventName="simulantCreated"
      hideOnSelect={true}
    />
  );
};

export default SimulantDesktop;
